/**
 * BentoGrid Layout System
 * Responsive grid layout inspired by Bento Box design
 * Supports flexible grid configurations and responsive breakpoints
 */

import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

// BentoGrid Types
export interface BentoGridProps {
  children: React.ReactNode;
  className?: string;
  columns?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  gap?: number;
  staggerChildren?: boolean;
}

export interface BentoGridItemProps {
  children: React.ReactNode;
  className?: string;
  colSpan?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  rowSpan?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  aspectRatio?: 'auto' | 'square' | 'video' | 'portrait' | 'landscape';
  priority?: 'high' | 'medium' | 'low';
}

// Grid configuration presets
export const gridPresets = {
  dashboard: {
    columns: { sm: 1, md: 2, lg: 3, xl: 4 },
    gap: 6,
  },
  analytics: {
    columns: { sm: 1, md: 2, lg: 2, xl: 3 },
    gap: 8,
  },
  metrics: {
    columns: { sm: 1, md: 3, lg: 4, xl: 6 },
    gap: 4,
  },
  showcase: {
    columns: { sm: 1, md: 2, lg: 3, xl: 3 },
    gap: 6,
  },
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95 
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

// Utility function to generate responsive grid classes
const generateGridClasses = (columns: BentoGridProps['columns']) => {
  const classes: string[] = ['grid'];
  
  if (columns?.sm) classes.push(`grid-cols-${columns.sm}`);
  if (columns?.md) classes.push(`md:grid-cols-${columns.md}`);
  if (columns?.lg) classes.push(`lg:grid-cols-${columns.lg}`);
  if (columns?.xl) classes.push(`xl:grid-cols-${columns.xl}`);
  
  return classes.join(' ');
};

// Utility function to generate responsive span classes
const generateSpanClasses = (
  colSpan?: BentoGridItemProps['colSpan'],
  rowSpan?: BentoGridItemProps['rowSpan']
) => {
  const classes: string[] = [];
  
  // Column span classes
  if (colSpan?.sm) classes.push(`col-span-${colSpan.sm}`);
  if (colSpan?.md) classes.push(`md:col-span-${colSpan.md}`);
  if (colSpan?.lg) classes.push(`lg:col-span-${colSpan.lg}`);
  if (colSpan?.xl) classes.push(`xl:col-span-${colSpan.xl}`);
  
  // Row span classes
  if (rowSpan?.sm) classes.push(`row-span-${rowSpan.sm}`);
  if (rowSpan?.md) classes.push(`md:row-span-${rowSpan.md}`);
  if (rowSpan?.lg) classes.push(`lg:row-span-${rowSpan.lg}`);
  if (rowSpan?.xl) classes.push(`xl:row-span-${rowSpan.xl}`);
  
  return classes.join(' ');
};

// Aspect ratio classes
const aspectRatioClasses = {
  auto: '',
  square: 'aspect-square',
  video: 'aspect-video',
  portrait: 'aspect-[3/4]',
  landscape: 'aspect-[4/3]',
};

// BentoGrid Component
export const BentoGrid: React.FC<BentoGridProps> = ({
  children,
  className,
  columns = { sm: 1, md: 2, lg: 3 },
  gap = 6,
  staggerChildren = true,
}) => {
  const gridClasses = generateGridClasses(columns);
  const gapClass = `gap-${gap}`;

  return (
    <motion.div
      className={cn(gridClasses, gapClass, 'w-full', className)}
      variants={staggerChildren ? containerVariants : undefined}
      initial={staggerChildren ? 'hidden' : undefined}
      animate={staggerChildren ? 'visible' : undefined}
    >
      {children}
    </motion.div>
  );
};

// BentoGridItem Component
export const BentoGridItem: React.FC<BentoGridItemProps> = ({
  children,
  className,
  colSpan,
  rowSpan,
  aspectRatio = 'auto',
  priority = 'medium',
}) => {
  const spanClasses = generateSpanClasses(colSpan, rowSpan);
  const aspectClass = aspectRatioClasses[aspectRatio];

  // Priority-based styling
  const priorityClasses = {
    high: 'ring-2 ring-blue-500/20 bg-gradient-to-br from-blue-50 to-white',
    medium: 'bg-white',
    low: 'bg-gray-50',
  };

  return (
    <motion.div
      className={cn(
        'relative overflow-hidden rounded-xl border border-gray-200 shadow-sm transition-all duration-300',
        'hover:border-gray-300 hover:shadow-md',
        spanClasses,
        aspectClass,
        priorityClasses[priority],
        className
      )}
      variants={itemVariants}
      whileHover={{ 
        y: -2,
        scale: 1.01,
        transition: { duration: 0.2 }
      }}
    >
      {children}
    </motion.div>
  );
};

// Pre-configured grid layouts for common use cases
export const DashboardGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <BentoGrid {...gridPresets.dashboard} className={className}>
    {children}
  </BentoGrid>
);

export const AnalyticsGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <BentoGrid {...gridPresets.analytics} className={className}>
    {children}
  </BentoGrid>
);

export const MetricsGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <BentoGrid {...gridPresets.metrics} className={className}>
    {children}
  </BentoGrid>
);

export const ShowcaseGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <BentoGrid {...gridPresets.showcase} className={className}>
    {children}
  </BentoGrid>
);

// Hook for responsive grid configuration
export const useResponsiveGrid = () => {
  const [gridConfig, setGridConfig] = React.useState(gridPresets.dashboard);

  React.useEffect(() => {
    const updateGrid = () => {
      const width = window.innerWidth;
      
      if (width < 768) {
        setGridConfig({ columns: { sm: 1, md: 1, lg: 1, xl: 1 }, gap: 4 });
      } else if (width < 1024) {
        setGridConfig({ columns: { sm: 1, md: 2, lg: 2, xl: 2 }, gap: 6 });
      } else if (width < 1280) {
        setGridConfig({ columns: { sm: 1, md: 2, lg: 3, xl: 3 }, gap: 6 });
      } else {
        setGridConfig({ columns: { sm: 1, md: 2, lg: 3, xl: 4 }, gap: 8 });
      }
    };

    updateGrid();
    window.addEventListener('resize', updateGrid);
    return () => window.removeEventListener('resize', updateGrid);
  }, []);

  return gridConfig;
};

// Grid layout analyzer for optimal arrangement
export const analyzeGridLayout = (items: { priority: string; size: string }[]) => {
  const priorityOrder = { high: 3, medium: 2, low: 1 };
  
  return items
    .map((item, index) => ({
      ...item,
      index,
      priorityScore: priorityOrder[item.priority as keyof typeof priorityOrder] || 1,
    }))
    .sort((a, b) => b.priorityScore - a.priorityScore);
};

export default BentoGrid;